// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const SharedContractView = ({ data = null, error = null, token = undefined }: { data?: any; error?: any; token?: string }) => {
  if (error) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: T.bg, fontFamily: "'Outfit', 'Segoe UI', sans-serif" }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <img src="/logo.png" alt="KontractAI" style={{ height: 48, marginBottom: 20 }} />
        <div style={{ fontSize: 18, fontWeight: 700, color: T.tx, marginBottom: 8 }}>Link No Longer Valid</div>
        <div style={{ fontSize: 13, color: T.txM, lineHeight: 1.6 }}>{error}</div>
      </div>
    </div>
  );

  if (!data) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: T.bg, fontFamily: "'Outfit', 'Segoe UI', sans-serif" }}>
      <div style={{ textAlign: "center", color: T.txM }}>Loading...</div>
    </div>
  );

  const Field = ({ label, value }: { label?: any; value?: any }) => value ? (
    <div style={{ padding: "10px 14px", backgroundColor: T.bg3, borderRadius: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: T.tx }}>{value}</div>
    </div>
  ) : null;

  const isRequest = data.type === 'request';
  const c = isRequest ? data.request : data.contract;

  return (
    <div style={{ minHeight: "100vh", backgroundColor: T.bg, fontFamily: "'Outfit', 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
          <img src="/logo.png" alt="KontractAI" style={{ height: 40 }} />
          {data.recipient?.name && <div style={{ fontSize: 12, color: T.txM }}>Shared with {data.recipient.name}{data.recipient.org ? ` (${data.recipient.org})` : ""}</div>}
        </div>

        {isRequest ? (
          <>
            <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "28px 32px", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <Badge c={T.ylw} b={T.ylwS}>Draft</Badge>
                    <div style={{ fontSize: 22, fontWeight: 800, color: T.tx, fontFamily: "'Source Serif 4', serif" }}>{c.title}</div>
                  </div>
                  {c.status && <div style={{ fontSize: 12, color: T.txM, marginTop: 4 }}>Status: {c.status}</div>}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
                <Field label="Request Type" value={c.agreement_category || c.request_type} />
                <Field label="Agreement Type" value={c.agreement_type || c.request_type} />
                <Field label="Counterparty" value={c.counterparty} />
                <Field label="Contact" value={c.counterparty_contact} />
                <Field label="Department" value={c.department} />
                <Field label="Business Owner" value={c.business_owner} />
                <Field label="Priority" value={c.priority} />
                <Field label="Requester" value={c.requester_name} />
                <Field label="Assigned To" value={c.assigned_name} />
                <Field label="Target Date" value={c.target_date ? fmtDate(c.target_date) : null} />
              </div>

              {c.description && (
                <div style={{ padding: "12px 14px", backgroundColor: T.bg3, borderRadius: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Description</div>
                  <div style={{ fontSize: 13, color: T.tx, lineHeight: 1.6, whiteSpace: "pre-line" }}>{c.description}</div>
                </div>
              )}
            </div>

            {(data.versions || []).length > 0 && (
              <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "24px 28px", marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>
                  {data.shared_version_id ? "Shared Document" : `Documents (${data.versions.length})`}
                </div>
                {data.versions.map((v: any, i: number) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", backgroundColor: T.bg3, borderRadius: 5, marginBottom: 4 }}>
                    <span style={{ fontSize: 14 }}>{v.file_type === "pdf" ? "📕" : "📄"}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: T.tx, fontWeight: 500 }}>{v.filename}</div>
                      <div style={{ fontSize: 10, color: T.txD }}>{v.uploader_name} · {fmtDate(v.created_at)}{v.notes ? ` · ${v.notes}` : ""}</div>
                    </div>
                    {v.has_file && <a href={`/api/share/view/${token}/version/${v.id}/document`} target="_blank" rel="noopener noreferrer" style={{ padding: "6px 14px", borderRadius: 5, backgroundColor: T.acc, color: "#fff", textDecoration: "none", fontSize: 11, fontWeight: 600 }}>View</a>}
                    <Badge c={T.acc} b={T.accS}>{(v.label || "").replace(/_/g, " ")}</Badge>
                    <Badge c={T.txM} b={T.bg4}>v{v.version_number}</Badge>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "28px 32px", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: T.tx, fontFamily: "'Source Serif 4', serif" }}>{c.name}</div>
                  {c.contract_title && c.contract_title !== c.name && <div style={{ fontSize: 13, color: T.txM, marginTop: 4 }}>{c.contract_title}</div>}
                </div>
                {data.has_document && <a href={`/api/share/view/${token}/document`} target="_blank" rel="noopener noreferrer" style={{ padding: "9px 20px", borderRadius: 6, backgroundColor: T.acc, color: "#fff", textDecoration: "none", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>View Document</a>}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
                <Field label="Contract Type" value={c.contract_type} />
                <Field label="Company" value={c.company_entity} />
                <Field label="Counterparty" value={c.counterparty_entity} />
                <Field label="Client" value={c.client_name} />
                <Field label="Effective Date" value={c.effective_date ? fmtDate(c.effective_date) : c.effective_date_verbatim} />
                <Field label="Termination Date" value={c.termination_date ? fmtDate(c.termination_date) : c.termination_date_verbatim} />
                <Field label="Governing Law" value={c.governing_law} />
                <Field label="Notice Period" value={c.notice_period} />
                <Field label="Renewal" value={(() => { const r = (c.renewal_term || "").toLowerCase(); if (r.startsWith("yes")) return "Yes"; if (r.includes("auto") || r.includes("renew") || r.includes("evergreen")) return "Yes"; if (r.startsWith("no")) return "No"; return c.auto_renewal ? `Yes (${c.renewal_term || 'auto'})` : c.renewal_term || "No"; })()} />
                <Field label="Department" value={c.department} />
                <Field label="Contract Value" value={c.contract_value} />
                <Field label="Business Owner" value={c.business_owner} />
              </div>
            </div>

            {(data.ai_fields || []).length > 0 && (
              <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "24px 28px", marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>Extracted Terms</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {data.ai_fields.map((f: any, i: number) => (
                    <div key={i} style={{ padding: "8px 12px", backgroundColor: T.bg3, borderRadius: 6 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: T.txD, textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 2 }}>{f.field_name}</div>
                      <div style={{ fontSize: 12, color: T.tx, lineHeight: 1.5 }}>{f.field_value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(data.children || []).length > 0 && (
              <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "24px 28px", marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>Related Contracts</div>
                {data.children.map((ch: any, i: number) => (
                  <div key={i} style={{ padding: "8px 0", borderBottom: `1px solid ${T.brdSubtle}`, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 12, color: T.tx }}>{ch.name}</span>
                    <span style={{ fontSize: 11, color: T.txD }}>{ch.relationship_type} · {ch.contract_type}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div style={{ textAlign: "center", padding: "20px 0", fontSize: 11, color: T.txD }}>
          Shared via KontractAI{data.expires_at ? ` · This link expires on ${fmtDate(data.expires_at)}` : ""}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ RESPONSE PAGE (public, no auth) ═══════════════════ */
